import { defineStore } from 'pinia';
import { useCookie } from '~/composables/useCookie';
import { useToastStore } from '~/stores/toast';

import { adjustBucketName } from '@/composables/bucketName';

const baseUrlCiBiz = import.meta.env['VITE_CI_BIZ_URL'];
const baseUrlProduct = import.meta.env['VITE_PRODUCT_URL'];
const baseUrlCellar = import.meta.env['VITE_CELLAR_URL'];

// @ts-ignore
export const useFileManagerStore = defineStore('FileManagerStore', {
  state: () => ({}),
  getters: {},
  actions: {
    async uploadFile(file: File) {
      const toastStore = useToastStore();
      const { getCookie } = useCookie();
      const filename = file.name;
      try {
        const { data, status, message } = await $fetch<any>(
          `${baseUrlCiBiz}/upload/${filename}`,
          {
            method: 'POST',
            body: file,
            headers: {
              Authorization: `Bearer ${getCookie('accessToken')}`,
              'Content-Length': file.size,
              'Content-Type': file.type,
            },
          }
        );
        if (status != 200) {
          throw 'error';
        }
        return data;
      } catch (err: any) {
        toastStore.showAlert({
          title: 'canNotConnectToServer',
          showCloseButton: true,
          timerProgressBar: true,
          type: 'error',
        });
        throw err;
      }
    },
    async uploadFileProductExcel(file: File) {
      const toastStore = useToastStore();
      const { getCookie } = useCookie();
      const formData = new FormData();
      formData.append('file', file);
      formData.append('content-type', file.type);

      try {
        const res = await $fetch<any>(`${baseUrlProduct}/products/importData`, {
          method: 'POST',
          body: formData,
          headers: {
            Authorization: `Bearer ${getCookie('accessToken')}`,
          },
        });
        return res;
      } catch (err: any) {
        console.error(err);
        toastStore.showAlert({
          title: 'canNotConnectToServer',
          showCloseButton: true,
          timerProgressBar: true,
          type: 'error',
        });
        throw err;
      }
    },

    async deleteFile(id: string) {
      const toastStore = useToastStore();
      try {
        const data = await $fetch<any>('/api/recording/file/remove', {
          method: 'POST',
          body: { id },
        });
        return true;
      } catch (err: any) {
        toastStore.showAlert({
          title: 'canNotConnectToServer',
          showCloseButton: true,
          timerProgressBar: true,
          type: 'error',
        });
        return false;
      }
    },

    async downloadFile(params: any, download: boolean = true) {
      const fileType = params.name?.split('.')[1];
      const { getCookie } = useCookie();
      const cookie = getCookie('accessToken');
      try {
        const serverResponse: Blob = await $fetch(
          `${baseUrlCiBiz}/download/${params.id}`,
          {
            method: 'GET',
            responseType: 'blob',
            headers: {
              Authorization: `Bearer ${cookie}`,
            },
          }
        );
        if (serverResponse) {
          const url = URL.createObjectURL(serverResponse);
          if (!download) return url;
          await this.downloadFileInSystem(url, params.name);
        }
      } catch (err) {
        await this.downloadFileCellar({
          ...params,
          id: params.id.split('.')[0],
          type: fileType,
        });
      }
    },

    // cellar
    async downloadFileCellar(params: any, download: boolean = true) {
      const { getCookie } = useCookie();
      const cookie = getCookie('accessToken');
      const type = adjustBucketName.getFileType(params.name);
      const res: any = await $fetch(
        `${baseUrlCellar}/download/${type}/${params.id}`,
        {
          method: 'GET',
          responseType: 'blob',
          headers: {
            Authorization: `Bearer ${cookie}`,
          },
        }
      );
      if (res) {
        const url = URL.createObjectURL(res);
        if (!download) return { url, file: res };
        this.downloadFileInSystem(url, params.name);
      }
    },

    async getFile(url: string, name: string) {
      const response = await fetch(url);
      const blob = await response.blob();

      const file = new File([blob], name, { type: blob.type });
      return file;
    },

    async downloadFileInSystem(url: string, name: string): Promise<boolean> {
      try {
        if (url.startsWith('blob:')) {
          // Handle Blob URL
          const response = await fetch(url);
          if (!response.ok) throw new Error('Blob not accessible');

          const blob = await response.blob();
          const blobUrl = URL.createObjectURL(blob);

          const element = document.createElement('a');
          element.href = blobUrl;
          element.download = name;
          element.style.display = 'none';
          document.body.appendChild(element);
          element.click();

          // Cleanup
          setTimeout(() => {
            document.body.removeChild(element);
            URL.revokeObjectURL(blobUrl); // Free memory
          }, 100);
        } else {
          // Handle regular HTTP URL
          const response = await fetch(url, { method: 'HEAD' });
          if (!response.ok) throw new Error('File not accessible');

          const element = document.createElement('a');
          element.href = url;
          element.download = name;
          element.style.display = 'none';
          document.body.appendChild(element);
          element.click();
          document.body.removeChild(element);
        }
        return true;
      } catch (error) {
        console.error('Download failed:', error);
      }
    },

    getFileName(val: string) {
      if (!val || !val.trim()) return '';
      let res = val.split('_');
      return res[res.length - 1];
    },
  },
});
